import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { SUBSTACK_PUBLICATION } from '../lib/substack';
import { boxSizingStyles } from '../styles/lit-shared';

const DEFAULT_TITLE = 'Te escribo cuando merezca la pena';
const DEFAULT_DESCRIPTION =
  'Cosas de Edge, agentes de IA y lo que voy aprendiendo construyendo en público. Sin promesa de “cada lunes”, sin spam y con baja en un clic.';
const COMPACT_TITLE = 'Newsletter';
const COMPACT_DESCRIPTION = 'Un correo cuando tenga algo útil que contar.';

@customElement('substack-subscribe')
export class SubstackSubscribe extends LitElement {
  @property({ type: String }) publication = SUBSTACK_PUBLICATION;
  @property({ type: String }) title = '';
  @property({ type: String }) description = '';
  @property({ type: String }) linkedin =
    'https://www.linkedin.com/in/alejandro-serrano-notivoli/';
  @property({ type: Boolean, reflect: true }) compact = false;

  @state() private email = '';
  @state() private isSubmitting = false;

  static styles = [
    boxSizingStyles,
    css`
      :host {
        display: block;
        font-family: var(--font-sans);
      }

      .subscribe {
        position: relative;
        color: var(--color-text);
        text-align: left;
      }

      /* Full (non-compact): cinematic band feel */
      :host(:not([compact])) .subscribe {
        padding: var(--space-xl) 0;
        overflow: hidden;
      }

      /* Compact (footer): open panel with hairline, no card glow */
      :host([compact]) .subscribe {
        padding: var(--space-lg) 0 0;
        border-top: 1px solid var(--color-border);
      }

      .section-label {
        display: block;
        margin-bottom: var(--space-md);
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        color: var(--color-accent);
      }

      :host([compact]) .section-label {
        margin-bottom: var(--space-sm);
      }

      h2 {
        margin: 0 0 var(--space-sm);
        font-size: clamp(1.5rem, 3vw, 2rem);
        font-weight: 700;
        letter-spacing: -0.03em;
        line-height: 1.2;
        color: var(--color-text);
      }

      :host([compact]) h2 {
        font-size: 1.125rem;
        letter-spacing: -0.02em;
      }

      h2 .title-grad {
        background: var(--gradient-text);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .description {
        margin: 0 0 var(--space-lg);
        color: var(--color-text-muted);
        font-size: 1.0625rem;
        line-height: 1.65;
        max-width: 52ch;
      }

      :host([compact]) .description {
        margin-bottom: var(--space-md);
        font-size: 0.875rem;
        line-height: 1.55;
      }

      .form-group {
        display: flex;
        flex-direction: column;
        gap: var(--space-sm);
      }

      @media (min-width: 640px) {
        .form-group {
          flex-direction: row;
        }
      }

      input[type='email'] {
        flex: 1;
        min-width: 0;
        padding: 0.875rem 1rem;
        border-radius: var(--radius-sm);
        border: 1px solid var(--color-border-strong);
        background: rgb(255 255 255 / 3%);
        color: var(--color-text);
        font-family: var(--font-sans);
        font-size: 0.95rem;
        outline: none;
        transition:
          border-color var(--duration-normal) var(--ease-out),
          box-shadow var(--duration-normal) var(--ease-out),
          background var(--duration-normal) var(--ease-out);
      }

      input[type='email']:focus {
        border-color: var(--color-accent);
        background: rgb(255 255 255 / 5%);
        box-shadow: 0 0 0 2px rgb(34 211 238 / 18%);
      }

      input[type='email']::placeholder {
        color: var(--color-text-subtle);
      }

      button.btn-submit {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
        padding: 0.875rem 1.5rem;
        border: none;
        border-radius: var(--radius-sm);
        background: var(--gradient-primary);
        color: var(--palette-white);
        font-family: var(--font-sans);
        font-weight: 700;
        font-size: 0.95rem;
        cursor: pointer;
        box-shadow: var(--shadow-glow-btn);
        transition:
          transform var(--duration-normal) var(--ease-out),
          box-shadow var(--duration-normal) var(--ease-out),
          opacity var(--duration-fast) var(--ease-out);
      }

      button.btn-submit:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: var(--shadow-glow-btn-hover);
      }

      button.btn-submit:active:not(:disabled) {
        transform: translateY(0);
      }

      button.btn-submit:disabled {
        opacity: 0.55;
        cursor: not-allowed;
        transform: none;
      }

      .divider {
        height: 1px;
        margin: var(--space-xl) 0 var(--space-lg);
        background: linear-gradient(
          to right,
          var(--color-accent),
          var(--color-border),
          transparent
        );
        opacity: 0.7;
      }

      :host([compact]) .divider {
        margin: var(--space-md) 0;
        background: var(--color-border);
        opacity: 1;
      }

      .social-section {
        display: flex;
        flex-direction: column;
        gap: var(--space-sm);
        align-items: flex-start;
      }

      @media (min-width: 640px) {
        .social-section {
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
        }
      }

      .social-title {
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--color-text-subtle);
      }

      .social-btn {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 0.875rem;
        border-radius: var(--radius-sm);
        border: 1px solid var(--color-border);
        background: transparent;
        color: var(--color-text-muted);
        font-size: 0.85rem;
        font-weight: 500;
        text-decoration: none;
        transition:
          background var(--duration-fast) var(--ease-out),
          color var(--duration-fast) var(--ease-out),
          border-color var(--duration-fast) var(--ease-out);
      }

      .social-btn:hover {
        background: var(--color-surface-hover);
        color: var(--color-text);
        border-color: var(--color-border-glow);
        text-decoration: none;
      }

      .social-btn svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
      }
    `,
  ];

  private get resolvedTitle(): string {
    if (this.title) return this.title;
    return this.compact ? COMPACT_TITLE : DEFAULT_TITLE;
  }

  private get resolvedDescription(): string {
    if (this.description) return this.description;
    return this.compact ? COMPACT_DESCRIPTION : DEFAULT_DESCRIPTION;
  }

  private handleInput(e: Event) {
    const input = e.target as HTMLInputElement;
    this.email = input.value;
  }

  private handleSubmit(e: Event) {
    e.preventDefault();
    if (!this.email || !this.email.includes('@')) return;

    this.isSubmitting = true;

    const substackUrl = `https://${this.publication}.substack.com/subscribe?email=${encodeURIComponent(this.email)}`;
    window.open(substackUrl, '_blank', 'noopener,noreferrer');

    setTimeout(() => {
      this.isSubmitting = false;
      this.email = '';
    }, 1000);
  }

  render() {
    const title = this.resolvedTitle;
    // Accent last word with gradient when title has 2+ words
    const words = title.trim().split(/\s+/);
    const titleHtml =
      words.length > 1
        ? html`${words.slice(0, -1).join(' ')}
            <span class="title-grad">${words[words.length - 1]}</span>`
        : html`<span class="title-grad">${title}</span>`;

    return html`
      <div class="subscribe">
        <span class="section-label">${this.compact ? 'Newsletter' : '06 · Newsletter'}</span>

        <h2>${titleHtml}</h2>
        <p class="description">${this.resolvedDescription}</p>

        <form class="form-group" @submit=${this.handleSubmit}>
          <input
            type="email"
            name="email"
            autocomplete="email"
            placeholder="tu@email.com"
            .value=${this.email}
            @input=${this.handleInput}
            required
            aria-label="Email para la newsletter"
          />
          <button type="submit" class="btn-submit" ?disabled=${this.isSubmitting}>
            ${this.isSubmitting ? 'Abriendo Substack…' : 'Suscribirme'}
          </button>
        </form>

        ${this.linkedin
          ? html`
              <div class="divider"></div>
              <div class="social-section">
                <span class="social-title">También estoy por aquí</span>
                <a
                  href=${this.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="social-btn"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9v8.37H9.25V10.9H6.46M7.86 6.72a1.47 1.47 0 1 0 0 2.94 1.47 1.47 0 0 0 0-2.94Z"
                    />
                  </svg>
                  LinkedIn
                </a>
              </div>
            `
          : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'substack-subscribe': SubstackSubscribe;
  }
}
